import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "react-toastify";
import CustomerLayout from "../../layouts/CustomerLayout";
import birthdayRequests from "../../api/Customer/BirthdayRequests";
import senderRequests from "../../api/Customer/SenderRequests";
import BirthdaySettingsModal from "./birthdays/BirthdaySettingsModal";
import {
  Cake,
  Settings2,
  RefreshCw,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getContactDisplayName(contact) {
  const fullName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
  return fullName || contact.phone_number || "Unknown contact";
}

// The API returns birthday as a full ISO datetime ("2026-08-19T00:00:00.000000Z"),
// not a plain "YYYY-MM-DD" string - slicing to the date portion first keeps
// this working for either shape (a plain date string round-tripped from the
// <input type="date"> edit form is still just the first 10 characters of itself).
function extractDatePart(birthdayString) {
  if (!birthdayString) return [null, null, null];
  const [birthYear, birthMonth, birthDay] = birthdayString.slice(0, 10).split("-").map(Number);
  return [birthYear, birthMonth, birthDay];
}

// Formats a birthday for display. Deliberately does NOT do
// `new Date(birthdayString)` directly - that parses as UTC midnight, which
// can render a day early in negative-UTC-offset timezones. Building the
// Date from its year/month/day parts avoids that.
function formatBirthdayDate(birthdayString) {
  const [birthYear, birthMonth, birthDay] = extractDatePart(birthdayString);
  if (!birthYear || !birthMonth || !birthDay) return "—";
  const birthdayDate = new Date(birthYear, birthMonth - 1, birthDay);
  return birthdayDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// Works out the next upcoming occurrence of a birthday (this year, or next
// year if it already passed) and the age the contact turns on that date.
function getUpcomingBirthdayInfo(birthdayString) {
  const [birthYear, birthMonth, birthDay] = extractDatePart(birthdayString);
  if (!birthYear || !birthMonth || !birthDay) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let nextOccurrenceYear = today.getFullYear();
  let nextOccurrence = new Date(nextOccurrenceYear, birthMonth - 1, birthDay);
  if (nextOccurrence < today) {
    nextOccurrenceYear += 1;
    nextOccurrence = new Date(nextOccurrenceYear, birthMonth - 1, birthDay);
  }

  const daysUntil = Math.round((nextOccurrence - today) / (1000 * 60 * 60 * 24));
  const turningAge = nextOccurrenceYear - birthYear;

  return { daysUntil, turningAge };
}

function formatDaysUntilLabel(daysUntil) {
  if (daysUntil === 0) return "Today";
  if (daysUntil === 1) return "Tomorrow";
  return `in ${daysUntil} days`;
}

// The real backend shape for this endpoint is unknown (it doesn't exist yet).
// Isolating the unwrapping here means only this one function needs updating
// once the real response shape is confirmed.
function normalizeBirthdaysResponse(response) {
  if (!response.success) {
    return { birthdays: [], pagination: null, loadFailed: true };
  }
  const payload = response.data?.data?.data || response.data?.data || response.data || {};
  const birthdaysList = Array.isArray(payload) ? payload : payload.data || payload.birthdays || [];
  const paginationPayload = Array.isArray(payload) ? {} : payload.pagination || {};

  return {
    birthdays: Array.isArray(birthdaysList) ? birthdaysList : [],
    pagination: {
      currentPage: paginationPayload.current_page || 1,
      perPage: paginationPayload.per_page || 15,
      total: paginationPayload.total ?? (Array.isArray(birthdaysList) ? birthdaysList.length : 0),
      lastPage: paginationPayload.last_page || 1,
      from: paginationPayload.from || 0,
      to: paginationPayload.to || 0,
    },
    loadFailed: false,
  };
}

const FILTER_TABS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

// ─── Pagination Controls (duplicated from ScheduledMessages.jsx's pattern —
// that component doesn't export its own, so this is a local equivalent) ─────
const PaginationControls = ({ pagination, onPageChange }) => {
  const { currentPage, lastPage, total, from, to } = pagination;
  if (total === 0) return null;

  const pageNumbers = Array.from({ length: Math.min(lastPage, 7) }, (unusedSlot, index) => {
    if (lastPage <= 7) return index + 1;
    if (currentPage <= 4) return index + 1;
    if (currentPage >= lastPage - 3) return lastPage - 6 + index;
    return currentPage - 3 + index;
  });

  return (
    <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:justify-between sm:items-center">
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{from}</span> to <span className="font-semibold">{to}</span> of{" "}
        <span className="font-semibold">{total}</span> birthday{total !== 1 ? "s" : ""}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${currentPage === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"}`}>
          <ChevronLeft className="w-4 h-4"/> Previous
        </button>
        <div className="flex gap-1">
          {pageNumbers.map((pageNumber) => (
            <button key={pageNumber} onClick={() => onPageChange(pageNumber)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${currentPage === pageNumber ? "bg-red-600 text-white" : "bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:border-red-300"}`}>
              {pageNumber}
            </button>
          ))}
        </div>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === lastPage}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${currentPage === lastPage ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"}`}>
          Next <ChevronRight className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Birthdays = () => {
  const [birthdays, setBirthdays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("today");

  const [pagination, setPagination] = useState({
    currentPage: 1, perPage: 15,
    total: 0, lastPage: 1, from: 0, to: 0,
  });

  const [allSenderIds, setAllSenderIds] = useState([]);
  const [isLoadingSenders, setIsLoadingSenders] = useState(true);

  const [birthdaySettings, setBirthdaySettings] = useState(null);

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination((previousPagination) => ({ ...previousPagination, currentPage: 1 }));
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const fetchBirthdays = useCallback(async (page, perPage, filter, search) => {
    setIsLoading(true);
    const response = await birthdayRequests.getBirthdays(filter, page, perPage, search);
    const normalized = normalizeBirthdaysResponse(response);
    setBirthdays(normalized.birthdays);
    setLoadFailed(normalized.loadFailed);
    if (normalized.pagination) {
      setPagination(normalized.pagination);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchBirthdays(pagination.currentPage, pagination.perPage, activeFilter, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, activeFilter, debouncedSearch]);

  // Fetch all sender IDs once, for the Settings modal's sender picker -
  // mirrors QuickSms.jsx's fetchSenderIds exactly, since SenderPickerList
  // expects this same shape.
  useEffect(() => {
    const fetchAllSenderIds = async () => {
      setIsLoadingSenders(true);
      try {
        const response = await senderRequests.getSenders(1, 100, "");
        const { data } = response;
        if (data?.data?.status_code === "000" && !data?.data?.in_error) {
          const senders = data?.data?.data?.data || data?.data?.data || [];
          setAllSenderIds(senders);
        }
      } catch (error) {
        // Non-critical prefetch — the sender picker just shows empty/loading.
      } finally {
        setIsLoadingSenders(false);
      }
    };
    fetchAllSenderIds();
  }, []);

  const loadBirthdaySettings = useCallback(async () => {
    // Fails silently — this is a background prefetch, not a user-initiated
    // action, so no error toast here (e.g. before any settings are saved yet).
    const response = await birthdayRequests.getBirthdaySettings();
    if (response.success) {
      const settingsPayload = response.data?.data?.data || response.data?.data || {};
      setBirthdaySettings({
        sender_id: settingsPayload.sender_id || "",
        message: settingsPayload.message || "",
      });
    }
  }, []);

  useEffect(() => {
    loadBirthdaySettings();
  }, [loadBirthdaySettings]);

  const handleAddSender = async (senderName) => {
    try {
      const response = await senderRequests.addSenderId(senderName);
      const responseData = response?.data;
      const validationError = response?.validationError || null;
      if (validationError) {
        toast.error(validationError);
        return false;
      }
      if (responseData?.data?.status_code === "000" && !responseData?.data?.in_error) {
        const refreshResponse = await senderRequests.getSenders(1, 100, "");
        const refreshedData = refreshResponse?.data;
        if (refreshedData?.data?.status_code === "000" && !refreshedData?.data?.in_error) {
          const senders = refreshedData?.data?.data?.data || refreshedData?.data?.data || [];
          setAllSenderIds(senders);
        }
        toast.success("Sender ID submitted for approval!");
        return true;
      }
      toast.error(responseData?.data?.reason || "Failed to add sender ID");
      return false;
    } catch (error) {
      toast.error("An error occurred while adding the sender ID");
      return false;
    }
  };

  const handlePageChange = (newPage) => setPagination((previousPagination) => ({ ...previousPagination, currentPage: newPage }));
  const handleFilterChange = (filterValue) => { setActiveFilter(filterValue); setPagination((previousPagination) => ({ ...previousPagination, currentPage: 1 })); };
  const refresh = () => fetchBirthdays(pagination.currentPage, pagination.perPage, activeFilter, debouncedSearch);

  const activeFilterLabel = FILTER_TABS.find((tab) => tab.value === activeFilter)?.label || "";

  return (
    <CustomerLayout>
      <div className="px-4 pt-20 min-h-screen bg-white lg:pt-20 md:p-6">
        <div className="mx-auto max-w-6xl">

          {/* ── Page Header ── */}
          <div className="relative p-4 mb-6 bg-white border border-red-100 rounded-2xl shadow-sm md:p-6">
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-red-50/50 blur-xl -z-10"/>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-xl border border-red-200">
                  <Cake className="w-6 h-6 text-red-600"/>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800 sm:text-2xl md:text-3xl">Birthdays</h1>
                  <p className="text-sm text-gray-600 mt-1">Celebrate your contacts on their special day</p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                  <Users className="w-4 h-4 text-red-600"/>
                  <span className="text-sm font-medium text-red-700">
                    {pagination.total} {activeFilterLabel}
                  </span>
                </div>

                <button onClick={refresh}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
                  <RefreshCw className="w-3.5 h-3.5"/>
                  <span className="hidden sm:inline">Refresh</span>
                </button>

                <button onClick={() => setSettingsModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
                  <Settings2 className="w-3.5 h-3.5"/>
                  <span className="hidden sm:inline">Birthday Settings</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── Filters ── */}
          <div className="p-4 mb-6 bg-white rounded-2xl border border-red-100 shadow-sm">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                {FILTER_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleFilterChange(tab.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      activeFilter === tab.value
                        ? "bg-white text-red-600 shadow-sm border border-red-100"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                <input type="text" placeholder="Search by name or phone..." value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"/>
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          {isLoading ? (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="h-11 bg-gray-50 border-b border-gray-100"/>
              {[...Array(6)].map((unusedSlot, skeletonIndex) => (
                <div key={skeletonIndex} className="flex gap-4 px-4 py-3.5 border-b border-gray-50 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-40"/>
                  <div className="h-4 bg-gray-100 rounded w-24"/>
                  <div className="h-4 bg-gray-100 rounded w-16"/>
                </div>
              ))}
            </div>
          ) : birthdays.length === 0 ? (
            <div className="py-20 text-center bg-white rounded-2xl border border-gray-100">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                <Cake className="w-8 h-8 text-red-300"/>
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-1">
                {loadFailed ? "Birthdays aren't available yet" : `No birthdays ${activeFilter === "today" ? "today" : activeFilterLabel.toLowerCase()}`}
              </h3>
              <p className="text-sm text-gray-500">
                {loadFailed
                  ? "This feature is still being connected on our end — check back soon."
                  : debouncedSearch
                    ? `No results match "${debouncedSearch}".`
                    : "Nothing to celebrate in this range just yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        {["Contact", "Birthday", "Turns"].map((headerLabel) => (
                          <th key={headerLabel} className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">{headerLabel}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {birthdays.map((contact, contactIndex) => {
                        const upcomingBirthdayInfo = getUpcomingBirthdayInfo(contact.birthday);
                        return (
                          <motion.tr
                            key={contact.id}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: contactIndex * 0.03 }}
                            className="border-b border-gray-50 hover:bg-red-50/30 transition-colors"
                          >
                            <td className="px-4 py-3.5">
                              <div className="font-semibold text-sm text-gray-800">{getContactDisplayName(contact)}</div>
                              <div className="text-xs text-gray-500">{contact.phone_number || "—"}</div>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="text-sm text-gray-700">{formatBirthdayDate(contact.birthday)}</div>
                              {upcomingBirthdayInfo && (
                                <div className="text-xs text-gray-400">{formatDaysUntilLabel(upcomingBirthdayInfo.daysUntil)}</div>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border bg-purple-50 text-purple-700 border-purple-200">
                                {upcomingBirthdayInfo ? `Turning ${upcomingBirthdayInfo.turningAge}` : "—"}
                              </span>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <PaginationControls pagination={pagination} onPageChange={handlePageChange}/>
            </>
          )}

        </div>
      </div>

      {/* ── Modals ── */}
      <AnimatePresence>
        {settingsModalOpen && (
          <BirthdaySettingsModal
            key="settings"
            settings={birthdaySettings}
            senderIds={allSenderIds}
            isLoadingSenders={isLoadingSenders}
            onAddSender={handleAddSender}
            onClose={() => setSettingsModalOpen(false)}
            onSaved={loadBirthdaySettings}
          />
        )}
      </AnimatePresence>
    </CustomerLayout>
  );
};

export default Birthdays;
